import { useState, useEffect, useMemo } from 'react'
import { backendClient } from '../utils/backend'
import {
  UserCheck, UserX, Clock, CheckCircle2, XCircle,
  FileText, Search, AlertCircle, Loader2, Shield
} from 'lucide-react'

interface ClaimRequest {
  id: string
  player_id: string
  user_id: string
  status: 'pending' | 'approved' | 'rejected'
  evidence_url: string | null
  created_at: string
  updated_at: string
  player_name?: string
  player_email?: string
  claimant_email?: string
}

export default function AdminClaims() {
  const [claims, setClaims] = useState<ClaimRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [processingId, setProcessingId] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  const fetchClaims = async () => {
    setLoading(true)
    setError('')
    try {
      const { data: claimsData, error: claimsError } = await backendClient
        .from('claim_requests')
        .select('*')
        .order('created_at', { ascending: false })

      if (claimsError) throw claimsError

      const enriched = await Promise.all(
        (claimsData || []).map(async (claim) => {
          const [playerRes, userRes] = await Promise.all([
            backendClient.from('players').select('name, email').eq('id', claim.player_id).single(),
            backendClient.from('profiles').select('email').eq('id', claim.user_id).single(),
          ])
          return {
            ...claim,
            player_name: playerRes.data?.name || 'Unknown Player',
            player_email: playerRes.data?.email || '',
            claimant_email: userRes.data?.email || '',
          }
        })
      )

      setClaims(enriched)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load claim requests')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchClaims()
  }, [])

  const handleApprove = async (claim: ClaimRequest) => {
    setProcessingId(claim.id)
    try {
      const { error: updatePlayerError } = await backendClient
        .from('players')
        .update({
          user_id: claim.user_id,
          is_claimed: true,
          claim_verified_at: new Date().toISOString(),
          claim_requested_by: null,
        })
        .eq('id', claim.player_id)

      if (updatePlayerError) throw updatePlayerError

      const { error: updateClaimError } = await backendClient
        .from('claim_requests')
        .update({ status: 'approved', updated_at: new Date().toISOString() })
        .eq('id', claim.id)

      if (updateClaimError) throw updateClaimError

      setClaims((prev) =>
        prev.map((c) =>
          c.id === claim.id ? { ...c, status: 'approved' as const } : c
        )
      )
    } catch (err) {
      console.error('Failed to approve claim:', err)
      setError(err instanceof Error ? err.message : 'Failed to approve claim')
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (claim: ClaimRequest) => {
    setProcessingId(claim.id)
    try {
      const { error: updateClaimError } = await backendClient
        .from('claim_requests')
        .update({ status: 'rejected', updated_at: new Date().toISOString() })
        .eq('id', claim.id)

      if (updateClaimError) throw updateClaimError

      const { error: clearRequestError } = await backendClient
        .from('players')
        .update({ claim_requested_by: null })
        .eq('id', claim.player_id)

      if (clearRequestError) throw clearRequestError

      setClaims((prev) =>
        prev.map((c) =>
          c.id === claim.id ? { ...c, status: 'rejected' as const } : c
        )
      )
    } catch (err) {
      console.error('Failed to reject claim:', err)
      setError(err instanceof Error ? err.message : 'Failed to reject claim')
    } finally {
      setProcessingId(null)
    }
  }

  const filteredClaims = useMemo(() => {
    let result = claims
    if (filter !== 'all') {
      result = result.filter((c) => c.status === filter)
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      result = result.filter(
        (c) =>
          c.player_name?.toLowerCase().includes(q) ||
          c.player_email?.toLowerCase().includes(q) ||
          c.claimant_email?.toLowerCase().includes(q)
      )
    }
    return result
  }, [claims, filter, searchQuery])

  const pendingCount = claims.filter((c) => c.status === 'pending').length

  const statusConfig = {
    pending: { label: 'Pending', icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
    approved: { label: 'Approved', icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
    rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="text-green-500 animate-spin" size={32} />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield size={24} className="text-green-500" />
          <div>
            <h2 className="text-2xl font-bold text-white">Profile Claim Requests</h2>
            <p className="text-sm text-gray-400 mt-1">Review and manage player profile ownership claims</p>
          </div>
          {pendingCount > 0 && (
            <span className="px-2.5 py-1 text-[10px] font-black uppercase tracking-widest bg-yellow-500/20 text-yellow-400 rounded-full border border-yellow-500/30">
              {pendingCount} pending
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-500/20 bg-red-500/5 p-4 flex items-center gap-3">
          <AlertCircle size={18} className="text-red-400 shrink-0" />
          <p className="text-sm text-red-300">{error}</p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/5 rounded-xl text-white placeholder-gray-600 focus:ring-2 focus:ring-green-500/20 focus:border-green-500 outline-none transition text-sm"
          />
        </div>
        <div className="flex gap-1 bg-[#161b22] p-1 rounded-xl border border-gray-800">
          {(['all', 'pending', 'approved', 'rejected'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition ${
                filter === f ? 'bg-gray-800 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {filteredClaims.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-12 text-center">
          <UserCheck className="mx-auto text-gray-500 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white">No Claims Found</h3>
          <p className="text-gray-400 mt-2 text-sm">
            {filter === 'all' ? 'No profile claim requests yet.' : `No ${filter} claims.`}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClaims.map((claim) => {
            const config = statusConfig[claim.status]
            return (
              <div
                key={claim.id}
                className={`bg-[#161b22] border rounded-2xl p-5 transition ${
                  claim.status === 'pending' ? 'border-yellow-500/20 hover:border-yellow-500/30' : 'border-gray-800'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-2xl ${config.bg} flex items-center justify-center shrink-0`}>
                      <config.icon size={20} className={config.color} />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white">{claim.player_name}</h4>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Player ID: <span className="text-gray-500 font-mono">{claim.player_id.slice(0, 8)}...</span>
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Claimant: <span className="text-gray-300">{claim.claimant_email}</span>
                      </p>
                      <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-2">
                        Submitted {new Date(claim.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                  </div>

                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest rounded-full ${config.bg} ${config.color} border ${config.border} shrink-0`}>
                    <config.icon size={10} />
                    {config.label}
                  </span>
                </div>

                {claim.evidence_url && (
                  <div className="mt-4 p-3 bg-[#0d1117] rounded-xl border border-gray-800">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <FileText size={14} />
                      <span>Evidence Document</span>
                      <a
                        href={claim.evidence_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-green-400 hover:text-green-300 font-bold"
                      >
                        View
                      </a>
                    </div>
                  </div>
                )}

                {claim.status === 'pending' && (
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => handleApprove(claim)}
                      disabled={processingId === claim.id}
                      className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-500 rounded-xl text-white text-sm font-bold transition disabled:opacity-50"
                    >
                      {processingId === claim.id ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <CheckCircle2 size={16} />
                      )}
                      {processingId === claim.id ? 'Processing...' : 'Approve & Transfer'}
                    </button>
                    <button
                      onClick={() => handleReject(claim)}
                      disabled={processingId === claim.id}
                      className="flex items-center gap-2 px-5 py-2.5 bg-red-900/30 hover:bg-red-900/50 border border-red-900/50 rounded-xl text-red-400 text-sm font-bold transition disabled:opacity-50"
                    >
                      <UserX size={16} />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
